import { motion } from "framer-motion";
import { ArrowRight, Camera, Download, RefreshCw, Moon } from "lucide-react";
import { IMAGES } from "@/config/images";
import { JOURNEYS, JOURNEY_ORDER } from "@/data/journeys";
import { Overline, Ornament, Seal } from "@/components/Brand";
import { QUESTIONS } from "@/data/questions";

const NOTES = [
  { icon: Camera, text: "This is a snapshot of where you are right now." },
  { icon: Moon, text: "Your results can change as you evolve." },
  { icon: RefreshCw, text: "You are encouraged to retake the assessment after completing a pathway or journey." },
  { icon: Download, text: "Download your personalized results, because they are not permanently stored." },
];

const fade = (d = 0) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay: d } });

export default function Welcome({ onBegin }) {
  return (
    <main data-testid="welcome-screen" className="px-5 sm:px-10 pb-20">
      <section className="max-w-6xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center pt-6 sm:pt-12">
        <div>
          <motion.div {...fade(0)} className="flex items-center gap-4"><Seal size={88} /><Overline>Goddess University</Overline></motion.div>
          <motion.h1 {...fade(0.1)} className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#4A2A20] leading-[1.05] mt-5 font-medium">
            Discover Your <span className="italic gold-text">Goddess Journey</span>
          </motion.h1>
          <motion.p {...fade(0.2)} className="font-serif-display italic text-lg sm:text-xl text-[#9A5547] mt-4">
            Your Sacred Path to Remembering, Healing, Expanding & Creating
          </motion.p>
          <motion.div {...fade(0.3)} className="mt-8 space-y-4 text-[15px] sm:text-base leading-relaxed text-[#7D5A4B] max-w-xl">
            <p><em>There is no single path to becoming. Every Goddess arrives with her own experiences, desires, gifts, and opportunities for expansion.</em></p>
            <p><em>This assessment will help you discover where to begin, what to explore next, and how your Goddess Journeys can support one another.</em></p>
          </motion.div>
          <motion.div {...fade(0.4)} className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
            <button data-testid="begin-journey-btn" onClick={onBegin} className="btn-rose rounded-full px-9 py-4 text-base font-medium inline-flex items-center justify-center gap-2">
              Begin My Journey <ArrowRight className="w-4 h-4" />
            </button>
            <span className="text-sm text-[#7D5A4B]">About 10 minutes · {QUESTIONS.length} gentle questions</span>
          </motion.div>
        </div>
        <motion.div {...fade(0.2)} className="relative">
          <div className="absolute -inset-3 rounded-[2.5rem] border border-[#C9A24A]/40 rotate-2" />
          <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-[0_40px_80px_-40px_rgba(122,70,50,0.55)] border border-[#C9A24A]/25">
            <img src={IMAGES.welcomeHero} alt="A Goddess University student attending class with Rev. Goddess LaMeel teaching on her laptop" className="w-full h-full object-cover object-[60%_45%]" data-testid="welcome-hero-image" />
          </div>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto mt-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="welcome-notes">
          {NOTES.map(({ icon: Icon, text }) => (
            <div key={text} className="glass-card rounded-2xl p-5 flex gap-3">
              <Icon className="w-5 h-5 shrink-0 text-[#B76E60] mt-0.5" />
              <p className="text-sm text-[#4A2A20] leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto mt-24">
        <div className="flex flex-col items-start gap-3 mb-10">
          <Overline>The Four Goddess Journeys</Overline>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#4A2A20]">Every path is sacred</h2>
          <Ornament />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {JOURNEY_ORDER.map((id, i) => <JourneyPreview key={id} j={JOURNEYS[id]} i={i} />)}
        </div>
      </section>
    </main>
  );
}

const JourneyPreview = ({ j, i }) => (
  <motion.article
    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.1 }}
    data-testid={`journey-preview-${j.id}`}
    className={`group flex flex-col rounded-3xl overflow-hidden border border-[#C9A24A]/30 bg-white/70 ${i % 2 ? "lg:mt-10" : ""}`}
  >
    <div className="aspect-[4/5] overflow-hidden relative">
      <img src={j.image} alt={j.name} data-testid={`journey-preview-image-${j.id}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
    </div>
    <div className="p-5 flex-1" style={{ background: j.tint }}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#B8892F] mb-2">{j.number}</div>
      <h3 className="font-serif-display text-xl text-[#4A2A20]" style={{ color: j.accent }}>{j.name}</h3>
      <p className="text-sm text-[#7D5A4B] mt-2 leading-relaxed">{j.purpose}</p>
      <div className="text-xs text-[#B8892F] mt-3">{j.pathways.length} pathways</div>
    </div>
  </motion.article>
);
